import { store } from '../store';
import { ENVIRONMENT_TYPE, ADMIN_LIST } from '../config/env';

/**
 * Utility functions for user-related operations
 */

/**
 * Gets short environment name (dev, stage, prod)
 * @param env - The raw environment type string
 * @returns Short environment name or empty string
 */
const getShortEnv = (env?: string): string => {
  if (!env) return '';
  const e = env.toLowerCase();
  if (e.includes('dev')) return 'dev';
  if (e.includes('stag')) return 'stage';
  if (e.includes('prod')) return 'prod';
  return e.slice(0, 5);
};

/**
 * Gets the current logged-in username from Redux store
 * @returns The current username (email) or empty string if not available
 */
export const getCurrentUsername = (): string => {
  try {
    // Get user from Redux store
    const state = store.getState();
    const user = state.user;

    // Priority 1: username field (most preferred)
    if (user?.username) {
      return user.username;
    }

    // Priority 2: email field
    if (user?.email) {
      return user.email;
    }

    // Priority 3: Construct from first_name and last_name
    if (user?.first_name || user?.last_name) {
      const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
      if (fullName) {
        return fullName;
      }
    }

    // Priority 4: user ID as fallback
    if (user?.id) {
      return user.id;
    }

    return '';
  } catch (error) {
    console.warn('Error getting current username:', error);
    return '';
  }
};

/**
 * Gets the current active organization name from Redux store
 * @returns The current organization name or empty string if not available
 */
export const getCurrentOrganizationName = (): string => {
  try {
    // Get user from Redux store
    const state = store.getState();
    const user = state.user;
    const orgId = user?.last_used_organization_id;
    const orgs = user?.organizations;

    if (orgId && orgs) {
      const currentOrg = orgs.find((org) => org.id === orgId);
      if (currentOrg) {
        return currentOrg.name;
      }
    }

    return '';
  } catch (error) {
    console.warn('Error getting current organization name:', error);
    return '';
  }
};

/**
 * Creates a label with username prefix for API payloads
 * Also includes organization name prefix if available
 * @param baseLabel - The base label value (e.g., solution ID, draft ID, file name)
 * @param username - Optional username (will be fetched from Redux if not provided)
 * @returns Label with username prefix in format "env_orgName_username:baseLabel"
 *
 * @example
 * // Returns "dev_Org_user@example.com:solution-123"
 * createLabelWithUsername('solution-123')
 *
 * @example
 * // Returns "dev_custom-user:draft-456" (if no organization)
 * createLabelWithUsername('draft-456', 'custom-user')
 */
export const createLabelWithUsername = (baseLabel: string | undefined, username?: string): string => {
  const currentUsername = username || getCurrentUsername();
  const orgName = getCurrentOrganizationName();
  const orgFirstName = orgName ? orgName.split(' ')[0] : '';
  const envPrefix = getShortEnv(ENVIRONMENT_TYPE);

  // If no username or no baseLabel, return baseLabel as-is
  if (!currentUsername || !baseLabel) {
    return baseLabel || '';
  }

  // Construct label prefix: env_org_username
  let labelPrefix = currentUsername;
  if (orgFirstName) {
    labelPrefix = `${orgFirstName}_${labelPrefix}`;
  }
  if (envPrefix) {
    labelPrefix = `${envPrefix}_${labelPrefix}`;
  }

  return `${labelPrefix}:${baseLabel}`;
};

/**
 * Checks if the current logged-in user has permission to edit diagrams.
 * The user's email is retrieved from the persisted Redux state in localStorage ('persist:root').
 * It is checked against the list of authorized emails defined in the VITE_ADMIN_EMAIL environment variable.
 * @returns boolean indicating if the user has edit permission
 */
export const checkHasEditPermission = (): boolean => {
  try {
    const rootPersist = localStorage.getItem('persist:root');
    if (!rootPersist) return false;
    
    const parsedRoot = JSON.parse(rootPersist);
    if (!parsedRoot.user) return false;
    
    const userObj = JSON.parse(parsedRoot.user);
    const email = (userObj?.email || userObj?.username || '').toLowerCase();
    if (!email) return false;
    
    if (ADMIN_LIST) {
      let allowedEmails: string[] = [];
      try {
        allowedEmails = JSON.parse(ADMIN_LIST);
      } catch {
        // Fallback for comma separated string
        allowedEmails = ADMIN_LIST.replace(/[\[\]"']/g, '').split(',').map((s: string) => s.trim());
      }
      
      const lowerAllowedEmails = allowedEmails.map(e => e.toLowerCase());
      if (Array.isArray(lowerAllowedEmails) && lowerAllowedEmails.includes(email)) {
        return true;
      }
    }
  } catch (err) {
    console.error('Error checking diagram edit permission:', err);
  }
  return false;
};
